/**
 * +world-resources: meshes for breakables, hidden caches and reagent nodes (Venom Altar style).
 * Each kind has three part sets merged into one static mesh (objects.ts):
 *   intact   what you smash (KayKit / Halloween kit props + procedural Venom Altar shapes)
 *   remains  what is left on the floor once it broke (low sherds, planks, shells)
 *   hint     hidden caches only: the cracked mound + jade glint shown before discovery
 * Parts are local to the object's base centre (yd, +y up, facing +z). Geometries carry
 * position, normal and optionally color / uv (the builder fills the white atlas texel).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { WorldObjectKind } from '@keystone/sim';
import { venomProc } from '../themes/venomAltar/procs';
import { VENOM_PALETTE as P } from '../themes/venomAltar/theme';

export type PartSet = 'intact' | 'remains' | 'hint';
export const PART_INDEX: Readonly<Record<PartSet, number>> = { intact: 0, remains: 1, hint: 2 };

/** One placed source geometry (shared, not owned) with a local transform and colour multiplier. */
export interface ModelPart {
  geo: THREE.BufferGeometry;
  matrix: THREE.Matrix4;
  tint: readonly [number, number, number];
}

export interface KindModel {
  intact: ModelPart[];
  remains: ModelPart[];
  hint: ModelPart[];
  /** Height of the intact model (yd): sparkle / flash anchor. */
  height: number;
}

/** Sources the builder can draw from: kit props (may be null when a kit failed) and procs. */
export interface ModelSources {
  kit(id: string): THREE.BufferGeometry | null;
}

type Col = number | readonly [number, number, number];
const tmpC = new THREE.Color();
const lin = (c: Col): [number, number, number] => {
  if (typeof c === 'number') {
    tmpC.setHex(c);
    return [tmpC.r, tmpC.g, tmpC.b];
  }
  return [c[0], c[1], c[2]];
};

/** Bake a colour with a vertical AO gradient into a non-indexed copy (uv dropped). */
function paint(geo: THREE.BufferGeometry, color: Col, ao: [number, number] = [0.7, 1.08]): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (g !== geo) geo.dispose();
  g.deleteAttribute('uv');
  const pos = g.attributes['position'] as THREE.BufferAttribute;
  g.computeBoundingBox();
  const y0 = g.boundingBox!.min.y;
  const y1 = g.boundingBox!.max.y;
  const [r, gg, b] = lin(color);
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = y1 > y0 ? Math.min(1, Math.max(0, (pos.getY(i) - y0) / (y1 - y0))) : 1;
    const k = ao[0] + (ao[1] - ao[0]) * t;
    col[i * 3] = r * k;
    col[i * 3 + 1] = gg * k;
    col[i * 3 + 2] = b * k;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  if (!g.attributes['normal']) g.computeVertexNormals();
  return g;
}

const M = new THREE.Matrix4();
const Q = new THREE.Quaternion();
const E = new THREE.Euler();
const S = new THREE.Vector3();
const T = new THREE.Vector3();
function xf(geo: THREE.BufferGeometry, t: [number, number, number], r: [number, number, number] = [0, 0, 0], s: [number, number, number] = [1, 1, 1]): THREE.BufferGeometry {
  M.compose(T.set(...t), Q.setFromEuler(E.set(...r)), S.set(...s));
  geo.applyMatrix4(M);
  return geo;
}
function combine(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts, false)!;
  parts.forEach((p) => p.dispose());
  return g;
}
const box = (w: number, h: number, d: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0);
const cyl = (rt: number, rb: number, h: number, seg = 10): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg).translate(0, h / 2, 0);
const sphere = (r: number, ws = 10, hs = 7): THREE.BufferGeometry => new THREE.SphereGeometry(r, ws, hs);
const octa = (r: number): THREE.BufferGeometry => new THREE.OctahedronGeometry(r, 0);

/** Procedural shapes owned by the model cache (disposed with it). */
const GEN: Record<string, () => THREE.BufferGeometry> = {
  /** Broken urn: a low ring of curved sherds and a spill of dark soil. */
  sherds: () => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + 0.4;
      const piece = new THREE.SphereGeometry(0.34, 6, 3, 0, 0.9, Math.PI * 0.3, Math.PI * 0.35);
      parts.push(paint(xf(piece, [Math.cos(a) * 0.38, 0.02, Math.sin(a) * 0.38], [Math.PI * (0.45 + 0.1 * (i % 2)), a, 0.3 * i], [1, 0.7, 1]), i % 2 ? 0xa86c42 : 0xb88050, [0.75, 1]));
    }
    parts.push(paint(sphere(0.32, 10, 4).scale(1.2, 0.12, 1.1).translate(0, 0.01, 0), 0x3a2c20, [0.8, 1]));
    return combine(parts);
  },
  /** Smashed crate: planks flat on the floor. */
  planks: () => {
    const parts: THREE.BufferGeometry[] = [];
    const layout: [number, number, number, number][] = [
      [-0.25, 0.1, 0.4, 0.9],
      [0.2, -0.2, -0.6, 0.8],
      [0.05, 0.35, 1.3, 0.7],
      [-0.1, -0.3, 2.1, 0.55],
    ];
    layout.forEach(([x, z, yaw, len], i) => parts.push(paint(xf(box(len, 0.06, 0.16), [x, 0.03 * i, z], [0, yaw, 0.05 * i]), i % 2 ? 0x6b4a2e : 0x7a5434, [0.7, 1])));
    return combine(parts);
  },
  /** Torn cocoon husk, flattened. */
  husk: () => {
    const a = paint(new THREE.SphereGeometry(0.4, 10, 6, 0, Math.PI * 1.4, 0, Math.PI * 0.6).scale(1, 0.35, 1.5), 0xa9b096, [0.65, 0.95]);
    const b = paint(xf(new THREE.TorusGeometry(0.35, 0.03, 4, 12, Math.PI), [0.1, 0.04, 0], [Math.PI / 2, 0, 0.3]), 0xd2d6c2, [1, 1]);
    return combine([a, b]);
  },
  /** Silk-wrapped cocoon on a web nest: tall pale ovoid with banding and strands. */
  cocoon: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(xf(sphere(0.32, 12, 9), [0, 0.62, 0], [0.15, 0, 0.1], [1, 1.9, 1]), 0xc2c8b0, [0.72, 1.1]));
    for (let i = 0; i < 4; i++) parts.push(paint(xf(new THREE.TorusGeometry(0.29 - Math.abs(i - 1.5) * 0.045, 0.028, 4, 14), [0, 0.32 + i * 0.19, 0], [Math.PI / 2 + 0.25 * (i % 2 ? 1 : -1), 0, 0.1]), 0xe2e5d6, [1, 1]));
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.3;
      parts.push(paint(xf(cyl(0.012, 0.02, 0.7, 4), [Math.cos(a) * 0.28, 0, Math.sin(a) * 0.28], [Math.sin(a) * 0.5, 0, -Math.cos(a) * 0.5]), 0xd8dccb, [1, 1]));
    }
    parts.push(paint(sphere(0.45, 10, 4).scale(1, 0.12, 1), 0x8a8f78, [0.8, 1]));
    return combine(parts);
  },
  /** Offering bowl: bronze bowl on a stone foot, heaped with coins and a jade bead. */
  bowl: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(cyl(0.2, 0.26, 0.16, 10), P.stoneSandShadow, [0.7, 1]));
    parts.push(paint(xf(new THREE.SphereGeometry(0.42, 14, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), [0, 0.52, 0], [0, 0, 0], [1, 0.75, 1]), 0xc28a3c, [0.75, 1.1]));
    parts.push(paint(xf(new THREE.TorusGeometry(0.41, 0.04, 5, 18), [0, 0.52, 0], [Math.PI / 2, 0, 0]), P.gold, [1, 1.1]));
    for (let i = 0; i < 6; i++) {
      const a = i * 2.1;
      parts.push(paint(xf(cyl(0.11, 0.11, 0.04, 8), [Math.cos(a) * 0.17 * (i % 3) * 0.6, 0.48 + i * 0.025, Math.sin(a) * 0.15], [0.2 * (i % 2), a, 0.15]), 0xe0b450, [0.95, 1.1]));
    }
    parts.push(paint(xf(sphere(0.07, 6, 4), [0.08, 0.66, 0.02]), P.jadeLight, [1, 1.1]));
    return combine(parts);
  },
  /** Knocked-over bowl. */
  bowl_tipped: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(xf(new THREE.SphereGeometry(0.42, 14, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), [0, 0.3, 0.1], [1.4, 0, 0], [1, 0.75, 1]), 0xa8793a, [0.7, 1]));
    parts.push(paint(xf(cyl(0.2, 0.26, 0.16, 10), [0.45, 0.0, -0.2], [0, 0, 1.4]), P.stoneSandShadow, [0.7, 1]));
    return combine(parts);
  },
  /** Venom gland: three swollen green sacs on a fleshy root, dark veins. */
  gland: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(sphere(0.6, 12, 4).scale(1, 0.22, 1), 0x3a2a2a, [0.8, 1]));
    const sacs: [number, number, number, number][] = [
      [0, 0.55, 0, 0.42],
      [0.38, 0.36, 0.18, 0.28],
      [-0.3, 0.32, -0.22, 0.26],
    ];
    for (const [x, y, z, r] of sacs) {
      const g = paint(xf(sphere(r, 12, 9), [x, y, z], [0, 0, 0], [1, 1.15, 1]), 0x5fd06a, [0.55, 1.15]);
      // Dark veins painted from a cheap noise.
      const col = g.attributes['color'] as THREE.BufferAttribute;
      const pos = g.attributes['position'] as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const n = Math.sin(pos.getX(i) * 19 + pos.getY(i) * 7) * Math.sin(pos.getZ(i) * 17 - pos.getY(i) * 11);
        if (Math.abs(n) < 0.12) col.setXYZ(i, col.getX(i) * 0.35, col.getY(i) * 0.45, col.getZ(i) * 0.3);
      }
      parts.push(g);
      parts.push(paint(xf(sphere(r * 0.25, 6, 4), [x + r * 0.35, y + r * 0.7, z + r * 0.35]), 0xc8ff9a, [1, 1]));
    }
    return combine(parts);
  },
  /** Burst gland: flat green splat and a deflated sac. */
  gland_burst: () => {
    const a = paint(sphere(0.75, 12, 4).scale(1, 0.05, 0.9), 0x2f7a3a, [0.9, 1]);
    const b = paint(xf(new THREE.SphereGeometry(0.3, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.55), [0.1, 0.0, 0], [0, 0, 0], [1.2, 0.5, 1]), 0x3a6a3a, [0.6, 1]);
    const c = paint(sphere(0.6, 12, 4).scale(1, 0.18, 1), 0x3a2a2a, [0.8, 1]);
    return combine([c, a, b]);
  },
  /** Serpent's Eye shard: amber crystals bursting from a rock. */
  crystals: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(xf(sphere(0.55, 7, 4), [0, 0.05, 0], [0, 0.4, 0], [1, 0.45, 0.9]), P.stoneCliff, [0.6, 1]));
    const xs: [number, number, number, number, number][] = [
      [0, 0.25, 0, 1.0, 0],
      [0.3, 0.18, 0.1, 0.65, 0.5],
      [-0.25, 0.15, 0.2, 0.55, -0.55],
      [0.05, 0.12, -0.3, 0.5, 0.35],
    ];
    for (const [x, y, z, h, lean] of xs) {
      parts.push(paint(xf(octa(0.2), [x, y + h * 0.5, z], [lean * 0.6, 0.3, lean], [0.9, h * 2.6, 0.9]), 0xffa040, [0.7, 1.25]));
    }
    return combine(parts);
  },
  crystal_stubs: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(xf(sphere(0.55, 7, 4), [0, 0.05, 0], [0, 0.4, 0], [1, 0.45, 0.9]), P.stoneCliff, [0.6, 1]));
    for (let i = 0; i < 3; i++) parts.push(paint(xf(octa(0.14), [Math.cos(i * 2.1) * 0.22, 0.24, Math.sin(i * 2.1) * 0.22], [0.3, i, 0.2], [1, 0.8, 1]), 0x8a5a30, [0.7, 1]));
    return combine(parts);
  },
  /** Hidden-cache hint: a cracked sandstone mound with a jade sliver catching the light. */
  hint: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(xf(sphere(0.75, 9, 5, ), [0, -0.05, 0], [0, 0.6, 0], [1.1, 0.42, 0.95]), P.stoneSandShadow, [0.65, 1.0]));
    for (let i = 0; i < 4; i++) {
      const a = i * 1.7 + 0.3;
      parts.push(paint(xf(box(0.32, 0.2, 0.26), [Math.cos(a) * 0.55, 0, Math.sin(a) * 0.5], [0.2, a, 0.15]), P.stoneSand, [0.7, 1]));
    }
    parts.push(paint(xf(octa(0.09), [0.12, 0.3, 0.18], [0.5, 0.2, 0.3], [0.8, 1.6, 0.8]), P.jadeLight, [1, 1.2]));
    return combine(parts);
  },
  /** Fallback chest (when the dungeon kit failed to load): wood box, rounded lid, gold bands. */
  chest: () => {
    const parts: THREE.BufferGeometry[] = [];
    parts.push(paint(box(1.3, 0.7, 0.9), P.wood, [0.7, 1]));
    parts.push(paint(xf(new THREE.CylinderGeometry(0.45, 0.45, 1.3, 10, 1, false, 0, Math.PI), [0, 0.7, 0], [0, 0, Math.PI / 2]), 0x7a5434, [0.9, 1.1]));
    for (const x of [-0.45, 0.45]) parts.push(paint(box(0.1, 1.17, 0.94).translate(x, 0, 0), P.gold, [0.85, 1.1]));
    return combine(parts);
  },
  /** Dirt bed under an egg clutch. */
  nest: () => paint(sphere(0.62, 12, 4).scale(1, 0.14, 1), 0x5a4a32, [0.75, 1]),
};

/** Kind → part layout. `kit:` ids come from the theme kits, `proc:` from Venom procs, `gen:` from GEN. */
interface PartSpec {
  src: string;
  t?: [number, number, number];
  r?: [number, number, number];
  s?: number | [number, number, number];
  tint?: [number, number, number];
  /** Used when the source is missing (a kit failed to load). */
  fallback?: string;
}
interface KindSpec {
  intact: PartSpec[];
  remains: PartSpec[];
  hint?: PartSpec[];
  height: number;
}

const KINDS: Readonly<Record<WorldObjectKind, KindSpec>> = {
  urn: { intact: [{ src: 'proc:urn', s: 1.08 }], remains: [{ src: 'gen:sherds' }], height: 1.05 },
  crate: {
    intact: [{ src: 'kit:kd_box_small_decorated', s: 0.78, fallback: 'gen:planks' }],
    remains: [{ src: 'gen:planks' }],
    height: 1.15,
  },
  bone_pile: {
    intact: [
      { src: 'kit:kh_skull', s: 0.85, t: [0.05, 0, 0.05], fallback: 'proc:rubble' },
      { src: 'kit:kh_bone_A', s: 1.3, t: [-0.3, 0.12, 0.15], r: [0, 0.8, 0] },
      { src: 'kit:kh_bone_C', s: 1.2, t: [0.2, 0.12, -0.3], r: [0, -0.5, 0] },
      { src: 'kit:kh_bone_A', s: 1.2, t: [0.32, 0.12, 0.28], r: [0, 2.2, 0] },
    ],
    remains: [
      { src: 'kit:kh_bone_C', s: 1.1, t: [-0.2, 0.1, 0], r: [0, 1.1, 0], fallback: 'gen:sherds' },
      { src: 'kit:kh_bone_A', s: 1.0, t: [0.3, 0.1, 0.2], r: [0, -0.3, 0] },
    ],
    height: 0.85,
  },
  egg_clutch: {
    intact: [
      { src: 'gen:nest' },
      { src: 'proc:egg', s: 0.82, t: [-0.18, 0, 0.08], r: [0, 0, 0.12] },
      { src: 'proc:egg', s: 0.74, t: [0.24, 0, -0.06], r: [0.1, 0, -0.15] },
      { src: 'proc:egg', s: 0.66, t: [0.02, 0, -0.3], r: [-0.12, 0, 0] },
    ],
    remains: [{ src: 'gen:nest' }, { src: 'proc:eggshell', s: 0.9 }, { src: 'proc:eggshell', s: 0.7, t: [0.2, 0, -0.25], r: [0, 2, 0] }],
    height: 0.95,
  },
  cocoon: { intact: [{ src: 'gen:cocoon' }], remains: [{ src: 'gen:husk' }], height: 1.25 },
  offering_bowl: { intact: [{ src: 'gen:bowl' }], remains: [{ src: 'gen:bowl_tipped' }], height: 0.75 },
  hidden_cache: {
    intact: [{ src: 'kit:kd_chest', s: 0.95, fallback: 'gen:chest' }],
    remains: [{ src: 'kit:kd_chest', s: 0.95, tint: [0.55, 0.5, 0.48], fallback: 'gen:chest' }],
    hint: [{ src: 'gen:hint' }],
    height: 1.3,
  },
  // +field: a field treasure chest (bigger than a cache, no hint mark: its light beam calls you).
  treasure_chest: {
    intact: [{ src: 'kit:kd_chest', s: 1.25, fallback: 'gen:chest' }],
    remains: [{ src: 'kit:kd_chest', s: 1.25, tint: [0.6, 0.55, 0.5], fallback: 'gen:chest' }],
    height: 1.6,
  },
  venom_gland: { intact: [{ src: 'gen:gland' }], remains: [{ src: 'gen:gland_burst' }], height: 1.1 },
  eye_shard: { intact: [{ src: 'gen:crystals' }], remains: [{ src: 'gen:crystal_stubs' }], height: 1.4 },
};

export const WORLD_OBJECT_KINDS = Object.keys(KINDS) as WorldObjectKind[];

/**
 * Builds and caches kind models. Owns the GEN / proc geometries it created (dispose()); kit
 * geometries belong to ToonAssets and are shared.
 */
export class ModelLibrary {
  private readonly gen = new Map<string, THREE.BufferGeometry>();
  private readonly procCache = new Map<string, THREE.BufferGeometry | null>();
  private readonly models = new Map<WorldObjectKind, KindModel>();

  constructor(private readonly sources: ModelSources) {}

  private source(src: string): THREE.BufferGeometry | null {
    const [ns, id] = src.split(':') as [string, string];
    if (ns === 'kit') return this.sources.kit(id);
    if (ns === 'proc') return venomProc(id, this.procCache);
    let g = this.gen.get(id);
    if (!g && GEN[id]) {
      g = GEN[id]();
      this.gen.set(id, g);
    }
    return g ?? null;
  }

  private parts(specs: readonly PartSpec[] | undefined): ModelPart[] {
    const out: ModelPart[] = [];
    for (const p of specs ?? []) {
      const geo = this.source(p.src) ?? (p.fallback ? this.source(p.fallback) : null);
      if (!geo) continue;
      const s = p.s ?? 1;
      const sc = typeof s === 'number' ? S.set(s, s, s) : S.set(...s);
      const m = new THREE.Matrix4().compose(T.set(...(p.t ?? [0, 0, 0])), Q.setFromEuler(E.set(...(p.r ?? [0, 0, 0]))), sc);
      out.push({ geo, matrix: m, tint: p.tint ?? [1, 1, 1] });
    }
    return out;
  }

  model(kind: WorldObjectKind): KindModel {
    let m = this.models.get(kind);
    if (!m) {
      const spec = KINDS[kind] ?? KINDS.urn;
      m = { intact: this.parts(spec.intact), remains: this.parts(spec.remains), hint: this.parts(spec.hint), height: spec.height };
      this.models.set(kind, m);
    }
    return m;
  }

  dispose(): void {
    this.gen.forEach((g) => g.dispose());
    this.procCache.forEach((g) => g?.dispose());
    this.gen.clear();
    this.procCache.clear();
    this.models.clear();
  }
}

/** Intact model height for a kind (sparkle / flash anchor), without building geometry. */
export function kindHeight(kind: WorldObjectKind): number {
  return (KINDS[kind] ?? KINDS.urn).height;
}
